/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useState, useContext } from "react";
import { Box } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import { Tooltip, IconButton } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { MdOutlineCancel, MdOutlineContentCopy } from "react-icons/md";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { MdRemoveCircleOutline } from "react-icons/md";
import RequestWhitelistAddressCancelConfirmModal from "../../shared/RequestWhitelistAddressCancelConfirmModal";
import RequestWhitelistAddressApproveConfirmModal from "../../shared/RequestWhitelistAddressApproveConfirmModal";
import RequestWhitelistAddressRejectConfirmModal from "../../shared/RequestWhitelistAddressRejectConfirmModal";
import RequestWhitelistAddressBlacklistConfirmModal from "../../shared/RequestWhitelistAddressBlacklistConfirmModal";
import { ProfileContext } from "../../../../store/ProfileProvider";
import SweetAlert2 from "react-sweetalert2";

dayjs.extend(utc);
dayjs.extend(timezone);

type Merchant = {
  id: string;
  name: string;
};

type Subaccount = {
  id: string;
  name: string;
};

type RowData = {
  wlad_id: string;
  status: string;
  address: string;
  asset: string;
  created_at: string;
  reason?: string;
  merchant: Merchant;
  subaccount: Subaccount;
};

const customDataGridStyles: Partial<GridClasses> = {
  root: "mui-table-custom",
  columnHeader: "text-start font-bold text-lg",
  row: "",
};

const WhitelistAddressList: React.FC<{
  userPermissions: any;
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
  triggerReloadList(): void;
}> = ({
  userPermissions,
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
  triggerReloadList,
}) => {
  const { profileTimeZone } = useContext(ProfileContext);
  const [swalProps, setSwalProps] = useState<any>({});

  // Cancel modal states
  const [whitelistAddressToCancel, setWhitelistAddressToCancel] =
    useState<any>(null);
  const [
    isOpenWhitelistAddressCancelConfirmModal,
    setIsOpenWhitelistAddressCancelConfirmModal,
  ] = useState<boolean>(false);

  // Approve modal states
  const [whitelistAddressToApprove, setWhitelistAddressToApprove] =
    useState<any>(null);
  const [
    isOpenWhitelistAddressApproveConfirmModal,
    setIsOpenWhitelistAddressApproveConfirmModal,
  ] = useState<boolean>(false);

  // Reject modal states
  const [whitelistAddressToReject, setWhitelistAddressToReject] =
    useState<any>(null);
  const [
    isOpenWhitelistAddressRejectConfirmModal,
    setIsOpenWhitelistAddressRejectConfirmModal,
  ] = useState<boolean>(false);

  // Blacklist modal states
  const [whitelistAddressToBlacklist, setWhitelistAddressToBlacklist] =
    useState<any>(null);
  const [
    isOpenWhitelistAddressBlacklistConfirmModal,
    setIsOpenWhitelistAddressBlacklistConfirmModal,
  ] = useState<boolean>(false);

  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const subaccountType = localStorage.getItem("subaccountType");

  interface WhitelistAddressestatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: WhitelistAddressestatusItems[] = [
    {
      status: "pending",
      value: "pending",
      display: (
        <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
      ),
    },
    {
      status: "approved",
      value: "approved",
      display: (
        <Badge className={`${statusClasses} status-approved`}>Approved</Badge>
      ),
    },
    {
      status: "rejected",
      value: "rejected",
      display: (
        <Badge className={`${statusClasses} status-rejected`}>Rejected</Badge>
      ),
    },
    {
      status: "cancelled",
      value: "cancelled",
      display: (
        <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge>
      ),
    },
    {
      status: "blacklist",
      value: "blacklist",
      display: (
        <Badge className={`${statusClasses} status-blacklist`}>Blacklist</Badge>
      ),
    },
  ];

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "",
      flex: 1,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<RowData, any, string>) => (
        <Tooltip title="Copy ID" arrow placement="top">
          <div>
            <IconButton
              id={"button-copy-id-" + params?.row?.wlad_id}
              onClick={() => handleClickCopy(params.row.wlad_id)}
            >
              <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
            </IconButton>
          </div>
        </Tooltip>
      ),
    },
    {
      field: "created_at",
      headerName: "Created Date",
      sortable: false,
      flex: 1,
      minWidth: 180,
      maxWidth: 180,
      renderCell: (params: GridRenderCellParams<RowData, string>) =>
        formatDate(params.row.created_at),
    },
    {
      field: "subaccount",
      headerName: "Subaccount",
      sortable: false,
      minWidth: 160,
      maxWidth: 160,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) =>
        params.row.subaccount.name,
    },
    {
      field: "nickname",
      headerName: "Nickname",
      sortable: false,
      headerAlign: "left",
      align: "left",
      flex: 1,
      minWidth: 180,
      maxWidth: 180,
    },
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 80,
      maxWidth: 80,
    },
    {
      field: "network",
      headerName: "Network",
      sortable: false,
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 140,
      maxWidth: 140,
    },
    {
      field: "address",
      headerName: "Address",
      sortable: false,
      flex: 1,
      minWidth: 530,
      renderCell: (params: GridRenderCellParams<RowData, string>) =>
        params.row.address,
    },
    {
      field: "status",
      headerName: "",
      sortable: false,
      flex: 1,
      headerAlign: "center",
      align: "center",
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<RowData, any, string>) => {
        const status = params.value as string;
        const statusItem = statusList.find((item) => item.status === status);
        return (
          <>
            {userRole === "superadmin" &&
            (status === "rejected" || status === "blacklist") &&
            params.row.reason ? (
              <Tooltip title={params.row.reason} arrow placement="top">
                <div className="cursor-help">
                  {statusItem ? statusItem.display : null}
                </div>
              </Tooltip>
            ) : (
              <>{statusItem ? statusItem.display : null}</>
            )}
          </>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      headerAlign: "right",
      align: "right",
      flex: 1,
      minWidth: 55,
      renderCell: (params) => {
        const { status } = params?.row || {};
        const canCancel =
          userRole === "subaccount" &&
          userPermissions?.cancelWhitelistAddressRequest === true &&
          status === "pending";
        const canApprove =
          userRole === "superadmin" &&
          userPermissions?.approveWhitelistAddress === true &&
          status === "pending";
        const canReject =
          userRole === "superadmin" &&
          userPermissions?.rejectWhitelistAddress === true &&
          status === "pending";
        const canBlacklist =
          userRole === "superadmin" &&
          userPermissions?.blacklistWhitelistAddress === true &&
          status === "approved";
        return (
          <>
            {canCancel && (
              <Tooltip title="Mark as Cancel" arrow placement="top">
                <div>
                  <IconButton
                    id={`button-cancel-${params?.row?.wlad_id}`}
                    onClick={() => handleClickCancel(params.row.wlad_id)}
                  >
                    <MdOutlineCancel className="status-cancelled h-5 w-5" />
                  </IconButton>
                </div>
              </Tooltip>
            )}
            {canApprove && (
              <Tooltip title="Mark as Approve" arrow placement="top">
                <div>
                  <IconButton
                    id={`button-approve-${params?.row?.wlad_id}`}
                    onClick={() => handleClickApprove(params.row.wlad_id)}
                  >
                    <AiOutlineCheckCircle className="status-approved h-5 w-5" />
                  </IconButton>
                </div>
              </Tooltip>
            )}
            {canReject && (
              <Tooltip title="Mark as Reject" arrow placement="top">
                <div>
                  <IconButton
                    id={`button-reject-${params?.row?.wlad_id}`}
                    onClick={() => handleClickReject(params.row.wlad_id)}
                  >
                    <MdOutlineCancel className="status-rejected h-5 w-5" />
                  </IconButton>
                </div>
              </Tooltip>
            )}
            {canBlacklist && (
              <Tooltip title="Mark as Blacklist" arrow placement="top">
                <div>
                  <IconButton
                    id={`button-blacklist-${params?.row?.wlad_id}`}
                    onClick={() => handleClickBlacklist(params.row.wlad_id)}
                  >
                    <MdRemoveCircleOutline className="status-rejected h-5 w-5" />
                  </IconButton>
                </div>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const formatAddress = (address?: string): string => {
    if (!address || address.length < 24) return "N/A";
    return `${address.slice(0, 15)}...${address.slice(-9)}`;
  };

  // Cancel handlers
  const handleClickCancel = (wlad_id: any) => {
    const whitelistAddress = tableData.filter(
      (x: any) => x.wlad_id === wlad_id
    )[0];
    setWhitelistAddressToCancel(whitelistAddress);
    showWhitelistAddressCancelConfirmModal();
  };

  const showWhitelistAddressCancelConfirmModal = () => {
    setIsOpenWhitelistAddressCancelConfirmModal(true);
  };

  const closeWhitelistAddressCancelConfirmModal = () => {
    setWhitelistAddressToCancel(null);
    setIsOpenWhitelistAddressCancelConfirmModal(false);
  };

  // Approve handlers
  const handleClickApprove = (wlad_id: any) => {
    const whitelistAddress = tableData.filter(
      (x: any) => x.wlad_id === wlad_id
    )[0];
    setWhitelistAddressToApprove(whitelistAddress);
    showWhitelistAddressApproveConfirmModal();
  };

  const showWhitelistAddressApproveConfirmModal = () => {
    setIsOpenWhitelistAddressApproveConfirmModal(true);
  };

  const closeWhitelistAddressApproveConfirmModal = () => {
    setWhitelistAddressToApprove(null);
    setIsOpenWhitelistAddressApproveConfirmModal(false);
  };

  // Reject handlers
  const handleClickReject = (wlad_id: any) => {
    const whitelistAddress = tableData.filter(
      (x: any) => x.wlad_id === wlad_id
    )[0];
    setWhitelistAddressToReject(whitelistAddress);
    showWhitelistAddressRejectConfirmModal();
  };

  const showWhitelistAddressRejectConfirmModal = () => {
    setIsOpenWhitelistAddressRejectConfirmModal(true);
  };

  const closeWhitelistAddressRejectConfirmModal = () => {
    setWhitelistAddressToReject(null);
    setIsOpenWhitelistAddressRejectConfirmModal(false);
  };

  // Blacklist handlers
  const handleClickBlacklist = (wlad_id: any) => {
    const whitelistAddress = tableData.filter(
      (x: any) => x.wlad_id === wlad_id
    )[0];
    setWhitelistAddressToBlacklist(whitelistAddress);
    showWhitelistAddressBlacklistConfirmModal();
  };

  const showWhitelistAddressBlacklistConfirmModal = () => {
    setIsOpenWhitelistAddressBlacklistConfirmModal(true);
  };

  const closeWhitelistAddressBlacklistConfirmModal = () => {
    setWhitelistAddressToBlacklist(null);
    setIsOpenWhitelistAddressBlacklistConfirmModal(false);
  };

  const formatDate = (timestamp: string) => {
    return dayjs(Number(timestamp))
      .tz(profileTimeZone)
      .format("DD MMM YY - hh:mm A");
  };

  const filterColumns = () => {
    let rebuildColumns = [];
    if (userRole !== "superadmin") {
      if (!userPermissions?.cancelPayout && userRole !== "superadmin") {
        const excludeFields = ["subaccount", "actions"];
        if (subaccountType === "agent") {
          excludeFields.push("batch_id");
        }
        rebuildColumns = columns.filter(
          (column) => !!!excludeFields.includes(column?.field)
        );
      } else {
        const excludeFields = ["subaccount"];
        if (subaccountType === "agent") {
          excludeFields.push("batch_id");
        }
        rebuildColumns = columns.filter(
          (column) => !!!excludeFields.includes(column?.field)
        );
      }
    } else {
      rebuildColumns = columns;
    }
    return rebuildColumns;
  };

  return (
    <>
      {/* Cancel Modal */}
      {userRole === "subaccount" && whitelistAddressToCancel && (
        <RequestWhitelistAddressCancelConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenWhitelistAddressCancelConfirmModal}
          dataToCancel={whitelistAddressToCancel}
          onClose={() => closeWhitelistAddressCancelConfirmModal()}
        />
      )}

      {/* Approve Modal */}
      {userRole === "superadmin" && whitelistAddressToApprove && (
        <RequestWhitelistAddressApproveConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenWhitelistAddressApproveConfirmModal}
          dataToApprove={whitelistAddressToApprove}
          onClose={() => closeWhitelistAddressApproveConfirmModal()}
        />
      )}

      {/* Reject Modal */}
      {userRole === "superadmin" && whitelistAddressToReject && (
        <RequestWhitelistAddressRejectConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenWhitelistAddressRejectConfirmModal}
          dataToReject={whitelistAddressToReject}
          onClose={() => closeWhitelistAddressRejectConfirmModal()}
        />
      )}

      {/* Blacklist Modal */}
      {userRole === "superadmin" && whitelistAddressToBlacklist && (
        <RequestWhitelistAddressBlacklistConfirmModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadList}
          isOpen={isOpenWhitelistAddressBlacklistConfirmModal}
          dataToBlacklist={whitelistAddressToBlacklist}
          onClose={() => closeWhitelistAddressBlacklistConfirmModal()}
        />
      )}

      <Box className="card w-full">
        <DataGrid
          sx={{
            "& .MuiDataGrid-cell": {
              padding: "8px 16px",
            },
            "& .MuiDataGrid-columnHeader": {
              padding: "8px 16px",
            },
            "& .MuiTablePagination-input": {
              marginRight: "-20px",
            },
            "& .MuiTablePagination-displayedRows": {
              display: "none",
            },
          }}
          loading={isLoading}
          rows={tableData || []}
          columns={filterColumns()}
          getRowId={(row) => row.wlad_id}
          rowCount={rowCount}
          sortingMode="server"
          paginationMode="server"
          pageSizeOptions={[10, 25, 50, 100]}
          rowHeight={48}
          columnHeaderHeight={48}
          paginationModel={{ page: page, pageSize: pageSize }}
          onPaginationModelChange={(newValue) => setPaginationModel(newValue)}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          classes={customDataGridStyles}
          hideFooter={currentLastKey === "" && page === 0}
          autoHeight
          localeText={{
            noRowsLabel: "No data available",
          }}
        />
      </Box>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default WhitelistAddressList;
